import type { CommonResponse, EmptyResponse, PagingResponse } from '@/types/responseModel';
import { Request } from '@/utils/request';

import type { IMemoryId } from './agent';
import type { IAutoReplySetting } from './friend';
import type { IBriefIntroduction } from './visualization';

export enum HOMEPAGE_STATUS {
  OPEN = 'open',
  CLOSE = 'close'
}

export enum HOMEPAGE_LAYOUT_TYPE {
  AVATAR = 'avatar',
  COVER = 'cover'
}

export enum HOMEPAGE_PIC_TYPE {
  CLASS = 'class',
  IMMERSIVE = 'immersive'
}

export enum HOMEPAGE_TALK_TYPE {
  INTRO = 'intro',
  FEATURED = 'featured'
}

export enum HOMEPAGE_FRIEND_STATUS {
  STRANGER = 'STRANGER',
  FRIEND = 'FRIEND',
  PENDING_REQUEST = 'PENDING_REQUEST',
  REQUEST_RECEIVED = 'REQUEST_RECEIVED', // a已经对b发出了申请，a访问b的首页看到的状态
  DELETED = 'DELETED',
  DELETED_BY_OTHER = 'DELETED_BY_OTHER',
  BLOCK = 'BLOCK',
  BLOCK_BY_OTHER = 'BLOCK_BY_OTHER'
}

// 主页数据接口
export interface IHomepageData {
  username?: string;
  route?: string;
  avatar?: string;
  cover?: string;
  video?: string;
  introduction?: string;
  briefIntroduction?: IBriefIntroduction;
  friendStatus?: HOMEPAGE_FRIEND_STATUS;
  sessionId?: string;
  breakIceSession?: string;
  greeting?: string;
  createTime?: string;
  commonInterests?: string[];
  commonFriendNum?: number;
  showIceBreakerButton?: boolean;
  layout?: HOMEPAGE_LAYOUT_TYPE;
  userId?: string;
  defaultTone?: string;
  autoReply?: boolean; // 主页主人的
  autoReplySettings?: IAutoReplySetting;
  voiceSetting?: boolean; // 主页主人的 有无克隆声音
  defaultAutoReply?: boolean;
  mySettings?: {
    autoReply?: boolean; // 访问者的
  };
  fpAccountExists?: boolean; // 有fp且该fp下已经有账号
  visitUserId?: string;
  isLogin?: boolean;
  blockedByMe?: boolean; // 主页主人被我拉黑
  isBlocked?: boolean; // 我被主页主人拉黑
  remark?: string;
  relationRemark?: string;
  featuredNum?: number;

  // owner才有
  status?: HOMEPAGE_STATUS;
  memoryIds?: IMemoryId[];
}

// 设置主页请求参数接口
export interface IHomepageSettingsParams {
  route?: string;
  username?: string;
  avatar?: string;
  cover?: string;
  video?: string;
  introduction?: string;
  introTalkId?: string;
  featuredTalkIds?: string[];
  defaultTone?: string;
  memoryIds?: IMemoryId[];
  status?: HOMEPAGE_STATUS;
  layout?: HOMEPAGE_LAYOUT_TYPE;
}

export const getHomepage = (params: { route?: string; userId?: string }) => {
  return Request<CommonResponse<IHomepageData>>({
    url: '/rest/os/homepage',
    method: 'get',
    params
  });
};

// 设置主页接口
export const updateHomepageSettings = (params: IHomepageSettingsParams) => {
  return Request<CommonResponse<any>>({
    url: '/rest/os/homepage/settings',
    method: 'post',
    data: params
  });
};

export const updateHomepageRoute = (data: { route: string }) => {
  return Request<CommonResponse<any>>({
    url: '/rest/os/homepage/route',
    method: 'post',
    data
  });
};

export const coverToVideo = (data: { url: string }) => {
  return Request<CommonResponse<{ videoUrl: string }>>({
    url: '/rest/os/homepage/cover/to/video',
    method: 'post',
    data
  });
};

export interface IReqHomepageSpark {
  longitude?: number;
  latitude?: number;
  ownerUserId: string;
}

export interface IHomepageSpark {
  id: number;
  content: string;
  userId: string;
}

export const getHomepageSpark = (data?: IReqHomepageSpark) => {
  return Request<CommonResponse<IHomepageSpark> | EmptyResponse>({
    url: '/rest/os/spark/get',
    method: 'post',
    data: data || {}
  });
};

export const exposeHomepage = (route: string) => {
  return Request<CommonResponse<any>>({
    url: '/rest/os/homepage/exposure',
    method: 'post',
    data: {
      route
    }
  });
};

export interface IHomepageExposeInfo {
  pageViews: number;
  uniqueVisitors: number;
  friendsCount: number;
}

export const getHomepageExposeInfo = () => {
  return Request<CommonResponse<IHomepageExposeInfo>>({
    url: '/rest/os/homepage/statistics',
    method: 'get'
  });
};

export interface ISearchHomepage {
  userId: string;
  route: string;
  username?: string;
  avatar?: string;
  introduction?: string;
}

export const searchHomepage = (params: {
  keyword: string;
  pageNo?: number;
  pageSize?: number;
  scope: 'all' | 'friends';
}) => {
  return Request<PagingResponse<ISearchHomepage>>({
    url: '/rest/os/homepage/search',
    method: 'get',
    params
  });
};

export const setAutoReply = (data: { sessionId: string; autoReply: boolean }) => {
  return Request<CommonResponse<any>>({
    url: '/rest/os/friend/update/auto/reply',
    method: 'post',
    data
  });
};

export const setOtherAutoReply = (data: { sessionId: string; autoReply: boolean }) => {
  return Request<CommonResponse<any>>({
    url: '/rest/os/friend/update/friend/auto/reply',
    method: 'post',
    data
  });
};

export const skipClodChat = (params: { sessionId: string }) => {
  return Request<CommonResponse<EmptyResponse>>({
    url: '/rest/os/cold/skip/chat',
    method: 'get',
    params
  });
};
